import React, { useEffect, useState } from 'react';
import {
  computeMsrpSavings,
  formatHeroCurrency,
  getDisplayImageSrc,
  getOfferLink,
  getRetailerNameFromOffer,
  PRICE_GREEN,
} from './simpleProductPageShared';
import { formatMsrpReference } from './dbPdpPricePage';
import './ProductComparisonHero.css';
import OfferSavingsBadge from './OfferSavingsBadge';
import { getOfferSavings } from '../../domain/offerSavings';

const ProductComparisonHero = ({
  productName,
  productImage,
  fallbackImage,
  msrp,
  lowestOffer,
  lowestPriceLabel,
  lowestComparablePrice,
  tiedBestOffers,
  loading,
  theme,
  isMobile,
  imageOverlay,
  onAffiliateClick,
}) => {
  const [imageSrc, setImageSrc] = useState(() => getDisplayImageSrc(productImage));

  useEffect(() => {
    setImageSrc(getDisplayImageSrc(productImage));
  }, [productImage]);

  const handleImageError = (e) => {
    e.target.onerror = null;
    const fullSrc = getDisplayImageSrc(productImage);
    if (imageSrc !== fullSrc) {
      setImageSrc(fullSrc);
    } else if (fallbackImage) {
      setImageSrc(fallbackImage);
    } else {
      e.target.src = `https://dummyimage.com/480x320/eeeeee/666666&text=${encodeURIComponent(productName || 'Product')}`;
    }
  };

  const lowestPriceFormatted = lowestPriceLabel || formatHeroCurrency(lowestComparablePrice);
  const savings = computeMsrpSavings(msrp, lowestComparablePrice);
  const savingsFormatted = savings != null ? formatHeroCurrency(savings) : null;
  const sale = getOfferSavings(lowestOffer);
  const offerLink = getOfferLink(lowestOffer);
  const msrpReference = formatMsrpReference(msrp);
  const bestRetailer = getRetailerNameFromOffer(lowestOffer);
  const tiedOffers = Array.isArray(tiedBestOffers) ? tiedBestOffers : [];
  const isPriceTie = tiedOffers.length > 1;

  const renderDealCard = () => {
    if (loading) {
      return (
        <div className="pdp-hero-deal-card pdp-hero-deal-card--loading">
          <p className="pdp-hero-deal-loading">Loading verified prices...</p>
        </div>
      );
    }

    if (!lowestOffer || !lowestPriceFormatted) {
      return (
        <div className="pdp-hero-deal-card pdp-hero-deal-card--empty">
          <p className="pdp-hero-deal-empty-title" data-testid="pdp-hero-empty-title">
            Current price verification delayed
          </p>
          <p className="pdp-hero-deal-empty-sub">
            We check trusted retailers daily. Last-known retailer offers may still appear below.
          </p>
          {msrpReference ? (
            <p className="pdp-hero-msrp" data-testid="pdp-hero-msrp">
              {msrpReference}
            </p>
          ) : null}
        </div>
      );
    }

    return (
      <div className="pdp-hero-deal-card pdp-hero-deal-card--action">
        <span className="pdp-hero-deal-badge">
          {isPriceTie ? 'Current verified price' : 'Best current verified offer'}
        </span>
        <div className="pdp-hero-deal-price" data-testid="pdp-hero-best-price">
          {lowestPriceFormatted}
        </div>
        {isPriceTie ? (
          <p className="pdp-hero-offer-note" data-testid="pdp-hero-tie-note">
            Lowest verified price, tied at {tiedOffers.length} retailers
          </p>
        ) : (
          <p className="pdp-hero-offer-note" data-testid="pdp-hero-retailer">
            Current best offer at {bestRetailer}
          </p>
        )}
        {msrpReference ? (
          <p className="pdp-hero-msrp" data-testid="pdp-hero-msrp">
            {msrpReference}
          </p>
        ) : null}
        {savingsFormatted && (
          <div className="pdp-hero-savings-badge">
            Save {savingsFormatted} vs MSRP
          </div>
        )}
        {sale?.onSale && <OfferSavingsBadge savings={sale}/>}
        {offerLink ? (
          <a
            href={offerLink}
            target="_blank"
            rel="noopener noreferrer"
            className="pdp-hero-cta"
            data-testid="pdp-hero-cta"
            style={{ background: `linear-gradient(90deg, ${PRICE_GREEN}, ${PRICE_GREEN})` }}
            onClick={() => {
              try {
                if (typeof onAffiliateClick === 'function') {
                  onAffiliateClick(lowestOffer);
                }
              } catch {
                // Analytics must never block outbound navigation.
              }
            }}
          >
            Check price at {bestRetailer}
            <span aria-hidden="true" className="pdp-hero-cta-chevron">
              ›
            </span>
          </a>
        ) : (
          <span className="pdp-hero-cta pdp-hero-cta--disabled">
            Check price at {bestRetailer || 'retailer'}
            <span aria-hidden="true" className="pdp-hero-cta-chevron">
              ›
            </span>
          </span>
        )}
      </div>
    );
  };

  const hasImage = Boolean(imageSrc || getDisplayImageSrc(productImage));

  return (
    <section
      className={`pdp-hero ${isMobile ? 'pdp-hero--mobile' : 'pdp-hero--desktop'}`}
      style={{
        color: theme.colors.text,
      }}
    >
      <h1
        className="pdp-hero-title"
        style={{
          color: theme.isDarkMode ? '#fff' : '#1a1a1a',
        }}
      >
        {productName}
      </h1>
      <p className="pdp-hero-framing" data-testid="pdp-hero-framing">
        Current verified retailer prices for this product. Choose a current verified offer to buy.
      </p>

      <div className="pdp-hero-grid">
        <div className="pdp-hero-image-col">
          {hasImage ? (
            <div
              className="pdp-hero-image-card"
              style={{
                background: theme.isDarkMode ? '#ffffff' : '#ffffff',
              }}
            >
              <img
                src={imageSrc || getDisplayImageSrc(productImage)}
                alt={productName}
                className="pdp-hero-image"
                onError={handleImageError}
              />
              {imageOverlay}
            </div>
          ) : (
            <div
              className="pdp-hero-image-card pdp-hero-image-card--placeholder"
              style={{ color: theme.colors.textSecondary }}
            >
              No product image available
            </div>
          )}
        </div>

        <div className="pdp-hero-deal-col">{renderDealCard()}</div>
      </div>

      <p
        className="pdp-hero-trust-line"
        style={{ color: theme.colors.textSecondary }}
      >
        <span aria-hidden="true">🎯</span> Verified retailer prices
        <span className="pdp-hero-trust-sub">
          These are current verified store offers and can change after you click. MSRP is a
          reference price, not a live retailer price.
        </span>
      </p>
    </section>
  );
};

export default ProductComparisonHero;
